import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Nav,
  NavItem,
  Carousel,
  CarouselItem,
  CarouselControl,
  CarouselIndicators,
} from "reactstrap";
import LazyLoad from "react-lazyload";
import ResponsivePicture from "../../ResponsivePicture";
import { REACT_APP_SHOP_CDN_SERVER } from "../../../utils/env";
import { useSelector } from "react-redux";
import shopProductSelectors from "../../../redux/shop/products/selectors";

// Helper functions
const getAllVariantIds = (includedItems) =>
  includedItems
    .filter((obj) => obj.type === "variant")
    .map((variant) => parseInt(variant.id));

const filterVariantImages = (includedItems, variant, variantIds) =>
  includedItems.filter(
    (incl) =>
      incl.type === "image" &&
      incl.attributes.viewable_type === "Spree::Variant" &&
      (parseInt(incl.attributes.viewable_id) === parseInt(variant.id) ||
        !variantIds.includes(parseInt(incl.attributes.viewable_id)))
  );

const getImageSources = (url) => [
  {
    url: `${REACT_APP_SHOP_CDN_SERVER}${url}`,
    content_type: "image/jpeg",
    media: "(min-width: 0em)",
  },
];
const renderLoadingState = () => (
  <Col md={{ size: 12, offset: 0 }} xs={{ size: 10, offset: 1 }}>
    <div className="sk-loading w100 sk-b30 mt-2 sk-bg300" />
    {Array(4)
      .fill(0)
      .map((_, idx) => (
        <span
          key={idx}
          style={{ width: "100px", height: "100px" }}
          className="mr-1 sk-loading sk-line bg-light d-md-inline-block d-none"
        />
      ))}
  </Col>
);
const ShopProductImageDisplay = ({ currentVariant, productId }, context) => {
  const language = useSelector((state) => state.i18nState.lang);
  const shopProduct = useSelector((state) =>
    shopProductSelectors.selectProductById(state, language, productId)
  );

  const { shopProductDataAttributes, shopProductIncluded } = {
    shopProductDataAttributes: shopProduct?.data?.attributes,
    shopProductIncluded: shopProduct?.included,
  };

  const [selectedImage, setSelectedImage] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  if (!shopProductDataAttributes || !shopProductIncluded || !currentVariant) {
    return renderLoadingState();
  }

  const allVariantIds = getAllVariantIds(shopProductIncluded);
  const images = filterVariantImages(
    shopProductIncluded,
    currentVariant,
    allVariantIds
  );

  const handleNext = () => {
    if (animating) return;
    setActiveIndex((activeIndex + 1) % images.length);
  };

  const handlePrevious = () => {
    if (animating) return;
    setActiveIndex((activeIndex === 0 ? images.length : activeIndex) - 1);
  };

  const handleIndexClick = (newIndex) => {
    if (animating) return;
    setActiveIndex(newIndex);
  };

  const renderImageThumbnails = () => (
    <Nav>
      {images.map((image, idx) => (
        <NavItem
          key={image.id + "-" + idx}
          className="m-1 hv-pointer"
          onClick={() => handleIndexClick(idx)}
        >
          <LazyLoad>
            <ResponsivePicture
              mediumData={{
                sources: getImageSources(image.attributes.styles[1].url),
              }}
              altTitle={getImageAltText(idx)}
              className={
                activeIndex === idx ? "border border-primary" : "border"
              }
            />
          </LazyLoad>
        </NavItem>
      ))}
    </Nav>
  );

  const getImageAltText = (idx) =>
    `${shopProductDataAttributes?.name || context.t("Product Image")} ${idx}`;

  const renderImageSlides = () =>
    images.map((image, idx) => (
      <CarouselItem
        onExiting={() => setAnimating(true)}
        onExited={() => setAnimating(false)}
        key={image.id}
        className="bg-light text-center"
      >
        <ResponsivePicture
          mediumData={{
            sources: getImageSources(image?.attributes?.styles[9]?.url),
          }}
          altTitle={getImageAltText(idx)}
          skipLazyLoading={idx === 0}
        />
      </CarouselItem>
    ));

  return (
    <Col
      xs={{ size: 10, offset: 1 }}
      md={{ size: 5, offset: 0 }}
      className="mb-2"
    >
      {images.length > 1 ? (
        <Row noGutters>
          <Col md={12} className="bg-light text-center">
            <Carousel
              activeIndex={activeIndex}
              next={handleNext}
              previous={handlePrevious}
            >
              <CarouselIndicators
                items={images}
                activeIndex={activeIndex}
                onClickHandler={handleIndexClick}
              />
              {renderImageSlides()}
              <CarouselControl
                direction="prev"
                directionText="Previous"
                onClickHandler={handlePrevious}
              />
              <CarouselControl
                direction="next"
                directionText="Next"
                onClickHandler={handleNext}
              />
            </Carousel>
          </Col>
          <Col md={12} className="d-md-block d-none">
            {renderImageThumbnails()}
          </Col>
        </Row>
      ) : (
        <div className="bg-light text-center">
          <ResponsivePicture
            mediumData={{
              sources: getImageSources(images[0]?.attributes?.styles[9]?.url),
            }}
            altTitle={getImageAltText(0)}
            skipLazyLoading
          />
        </div>
      )}
    </Col>
  );
};

ShopProductImageDisplay.propTypes = {
  currentVariant: PropTypes.object,
};

ShopProductImageDisplay.contextTypes = {
  t: PropTypes.func,
};

export default ShopProductImageDisplay;
